import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppleLogo, GoogleLogo, DeviceMobile, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { isStandalonePWA } from '../utils/push';

/**
 * PWA install card shown on the Landing Page.
 * Visible to everyone with a hint that the app is only for registered users.
 * Logged-out visitors see a "Jetzt einloggen" CTA instead of install steps.
 */
export default function InstallAppCard() {
  const { user } = useAuth();
  const [isInstalled, setIsInstalled] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);

  useEffect(() => {
    setIsInstalled(isStandalonePWA());
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const triggerAndroidInstall = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    try { await installPromptEvent.userChoice; } catch (e) { /* ignore */ }
    setInstallPromptEvent(null);
  };

  if (isInstalled) return null;

  return (
    <section
      className="relative overflow-hidden bg-[#0F1115] text-white py-20 sm:py-24 px-4"
      data-testid="install-app-section"
    >
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 30%, #AB1C24 0px, transparent 50%), radial-gradient(circle at 80% 70%, #F5C842 0px, transparent 55%)',
      }} />
      <div className="relative max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
          <div className="flex-1 min-w-0">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#F5C842] mb-4">App · iPhone · Android</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Lerne Spanisch bequem von deinem Handy aus
            </h2>
            <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-xl mb-6">
              Installiere Habla als App auf deinem iPhone oder Android-Gerät und übe,
              wann und wo du willst — in der Bahn, im Wartezimmer oder gemütlich auf dem Sofa.
              Push-Benachrichtigungen erinnern dich an dein tägliches Thema und deine Serie.
            </p>
            <p className="text-white/90 text-sm sm:text-base font-medium mb-6">
              ⏱️ <strong className="text-[#F5C842]">In nur 10 Sekunden installiert</strong> — folge einfach den Schritten rechts.
            </p>
            <div className="inline-flex items-center gap-2 text-sm font-medium bg-amber-500/10 border border-amber-400/40 rounded-full px-4 py-2 mb-6 text-amber-200">
              <WarningCircle size={16} weight="fill" className="text-amber-300" />
              Nur für registrierte Kursteilnehmer
            </div>
            {!user && (
              <div>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 bg-[#AB1C24] hover:bg-[#C42028] text-white font-semibold px-5 py-3 rounded-xl transition-colors"
                  data-testid="install-login-cta"
                >
                  Jetzt einloggen, um zu installieren
                </Link>
              </div>
            )}
            {user && installPromptEvent && (
              <button
                type="button"
                onClick={triggerAndroidInstall}
                className="inline-flex items-center gap-2 bg-[#F5C842] hover:bg-[#F5C842]/90 text-[#0F1115] font-semibold px-5 py-3 rounded-xl transition-colors"
                data-testid="install-android-btn"
              >
                <DeviceMobile size={18} weight="fill" /> App installieren
              </button>
            )}
          </div>

          <div className="w-full lg:w-[460px] flex-shrink-0 grid gap-4" data-testid="install-steps">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-3">
                <AppleLogo size={22} weight="fill" className="text-white" />
                <h3 className="font-heading font-semibold text-lg">iPhone · iPad</h3>
                <span className="ml-auto text-xs text-white/50">~10 Sek.</span>
              </div>
              <ol className="list-decimal pl-5 space-y-1.5 text-[13px] text-white/80">
                <li>Öffne diese Seite in <strong>Safari</strong>.</li>
                <li>Tippe unten auf das <strong>Teilen-Symbol</strong>.</li>
                <li>Wähle <strong>Zum Home-Bildschirm</strong> und tippe <strong>Hinzufügen</strong>.</li>
                <li>Fertig — öffne die App vom Home-Bildschirm.</li>
              </ol>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-3">
                <GoogleLogo size={22} weight="fill" className="text-white" />
                <h3 className="font-heading font-semibold text-lg">Android · Chrome</h3>
                <span className="ml-auto text-xs text-white/50">~10 Sek.</span>
              </div>
              <ol className="list-decimal pl-5 space-y-1.5 text-[13px] text-white/80">
                <li>Öffne diese Seite in <strong>Chrome</strong>.</li>
                <li>Tippe oben rechts auf das <strong>Menü ⋮</strong>.</li>
                <li>Wähle <strong>App installieren</strong> oder <strong>Zum Startbildschirm hinzufügen</strong>.</li>
                <li>Fertig — die App liegt auf deinem Startbildschirm.</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
